import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignReadinessDto, CampaignScheduleDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'
import { queueNotice } from './campaignLabels.js'
import type { ScheduleOutcome } from './SchedulePanel.js'

/**
 * The last screen before a campaign leaves.
 *
 * Everything it shows is read fresh and read again after a test, because every
 * fact in it can change between writing a campaign and sending it: somebody
 * unsubscribes, a DNS record gets published, a test arrives. A checklist that
 * described the moment the page opened would be a checklist that goes stale
 * while somebody reads it.
 */

export type SendReviewState = AsyncState<CampaignReadinessDto>

/** The same shape the campaign list uses, because it is the same wording. */
type Notice = ReturnType<typeof queueNotice>

export function useSendReview({
  client,
  campaignId,
}: {
  readonly client: ApiClient
  readonly campaignId: string
}) {
  const load = useCallback(() => client.campaignReadiness(campaignId), [client, campaignId])
  const { state, reload } = useLoaded<CampaignReadinessDto>(
    load,
    'We could not check this campaign. Please try again in a moment.',
  )

  const [notice, setNotice] = useState<Notice | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  /** The moment as set on this screen, so the panel shows what was chosen. */
  const [scheduled, setScheduled] = useState<CampaignScheduleDto | undefined>(undefined)
  const [outcome, setOutcome] = useState<ScheduleOutcome | undefined>(undefined)

  const sendTest = useCallback(async () => {
    setBusy(true)
    setNotice(undefined)
    setProblem(undefined)
    try {
      const outcome = await client.testSendCampaign(campaignId)
      if (outcome.status !== 'queued') {
        setProblem('That campaign is no longer there.')
        return
      }
      // Reloaded, because the checklist item about testing has just changed.
      await reload()
      setNotice({
        tone: 'ok',
        text: 'On its way. It goes through the same queue as the real send, so give it a minute.',
      })
    } catch {
      setProblem('We could not send that test. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }, [client, campaignId, reload])

  /**
   * Queues it, and stays put.
   *
   * Navigating away on success would throw the one confirmation somebody wants
   * to read — how many messages, and that sending has started — onto a screen
   * they did not ask for. Reloading instead leaves the answer in front of them,
   * with the checklist now saying the campaign has gone.
   */
  const send = useCallback(async () => {
    setBusy(true)
    setNotice(undefined)
    setProblem(undefined)
    try {
      setNotice(queueNotice(await client.queueCampaign(campaignId)))
      await reload()
    } catch {
      // The request did not complete, so whether anything was queued is
      // unknown — and this is a send. Saying so is the only honest answer.
      setProblem('We could not tell whether that was queued. Reload before trying again.')
    } finally {
      setBusy(false)
    }
  }, [client, campaignId, reload])

  /**
   * Sets the moment instead of sending now.
   *
   * The counts in the answer are kept, because they are the only honest source
   * for them: the audience and how much of it has no timezone are resolved by
   * the same expression the queue insert uses, in the transaction that wrote
   * the schedule. A number this screen worked out for itself could disagree
   * with the one that actually sends.
   */
  const schedule = useCallback(
    async (wanted: CampaignScheduleDto) => {
      setBusy(true)
      setNotice(undefined)
      setProblem(undefined)
      try {
        const answer = await client.scheduleCampaign(campaignId, wanted)

        if (answer === 'not-found') {
          setProblem('That campaign is no longer there.')
          return
        }
        if (answer.status === 'not-a-draft') {
          setProblem('This campaign has already been queued, so its time cannot be changed.')
          return
        }
        if (answer.status === 'invalid') {
          setProblem(answer.problems.join(' '))
          return
        }

        setOutcome(answer)
        setScheduled(wanted)
        // Reloaded because the campaign is no longer a draft, and the checklist
        // says so.
        await reload()
      } catch {
        setProblem('We could not schedule that. Please try again in a moment.')
      } finally {
        setBusy(false)
      }
    },
    [client, campaignId, reload],
  )

  const unschedule = useCallback(async () => {
    setBusy(true)
    setProblem(undefined)
    try {
      await client.unscheduleCampaign(campaignId)
      setScheduled(undefined)
      setOutcome(undefined)
      await reload()
      setNotice({ tone: 'ok', text: 'Unscheduled. It is a draft again.' })
    } catch {
      setProblem('We could not unschedule that. Please try again in a moment.')
    } finally {
      setBusy(false)
    }
  }, [client, campaignId, reload])

  return {
    state,
    notice,
    problem,
    busy,
    scheduled,
    outcome,
    sendTest,
    send,
    schedule,
    unschedule,
    retry: reload,
  }
}
